// Összevetés (7.2 M4): every flow in shadow against what Klaviyo really sent.
// Per flow and country: the match rate over the last 1, 7 and 14 final days,
// the differences the owner's policy does not explain, the ones it does, what
// was out of window, the days since the last unexplained difference, the
// versions involved, and "élesíthető?" (owner decision 2: 1–2 weeks without
// an unexplained difference). A row opens the flow's own comparison.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { fetchFlowComparisonSummary } from "@/lib/api";
import type { FlowComparisonSummaryRow } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";
import { NativeSelect } from "@/components/ui/native-select";
import { DataTable } from "@/components/emc/DataTable";
import { Field } from "@/components/emc/Field";
import { Country } from "@/components/emc/Flag";
import { PageHeader } from "@/components/emc/PageHeader";
import { StatusPill } from "@/components/emc/StatusPill";
import { Toolbar } from "@/components/emc/Toolbar";
import { EmptyState, ErrorState } from "@/components/emc/states";
import { count } from "@/components/emc/format";
import { COUNTRIES } from "@/pages/flows/model";
import { rateText, rateTone, readyLook } from "@/pages/flows/compareModel";

function Rate({ row, days }: { row: FlowComparisonSummaryRow; days: string }) {
  const window = row.windows[days];
  if (!window || window.days === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <StatusPill
      tone={rateTone(window)}
      title={`${window.days} végleges nap · ${count(window.compared)} összevetett tétel · ${count(window.outcomes.match)} egyezik`}
    >
      {rateText(window)}
    </StatusPill>
  );
}

export default function FlowComparisonList() {
  const navigate = useNavigate();
  const [country, setCountry] = useState("");
  const [state, reload] = useLoad(() => fetchFlowComparisonSummary(country), [country]);
  const rows = state.data?.flows ?? [];

  const columns: ColumnDef<FlowComparisonSummaryRow>[] = [
    { id: "country", header: "Ország", cell: ({ row }) => <Country code={row.original.country} /> },
    { id: "name", header: "Flow", cell: ({ row }) => row.original.name, meta: { wrap: true } },
    { id: "r1", header: "Egyezés · 1 nap", cell: ({ row }) => <Rate row={row.original} days="1" /> },
    { id: "r7", header: "7 nap", cell: ({ row }) => <Rate row={row.original} days="7" /> },
    { id: "r14", header: "14 nap", cell: ({ row }) => <Rate row={row.original} days="14" /> },
    {
      id: "unexplained",
      header: "Nem magyarázott",
      cell: ({ row }) => count(row.original.windows["7"]?.unexplained ?? 0),
      meta: { align: "right", mono: true },
    },
    {
      id: "explained",
      header: "Magyarázott",
      cell: ({ row }) => count(row.original.windows["7"]?.explained ?? 0),
      meta: { align: "right", mono: true, muted: true },
    },
    {
      id: "missing_info",
      header: "Hiányzó adat",
      cell: ({ row }) => count(row.original.windows["7"]?.missing_info ?? 0),
      meta: { align: "right", mono: true, muted: true },
    },
    {
      id: "window",
      header: "Ablakon kívül",
      cell: ({ row }) => count(row.original.windows["7"]?.out_of_window ?? 0),
      meta: { align: "right", mono: true, muted: true },
    },
    {
      id: "since",
      header: "Utolsó eltérés óta",
      cell: ({ row }) =>
        row.original.days_since_unexplained !== null
          ? `${count(row.original.days_since_unexplained)} nap`
          : row.original.never_unexplained
            ? "nem volt"
            : "—",
      meta: { align: "right", mono: true },
    },
    {
      id: "today",
      header: "Ma eddig",
      cell: ({ row }) =>
        row.original.today === null ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span title={`${row.original.today.day}: ${count(row.original.today.compared)} tétel`}>
            {rateText(row.original.today)}
          </span>
        ),
      meta: { align: "right", mono: true },
    },
    {
      id: "versions",
      header: "Verziók",
      cell: ({ row }) => row.original.versions.map((each) => `${each.number ?? "?"}.`).join(", ") || "—",
      meta: { mono: true, muted: true },
    },
    {
      id: "ready",
      header: "Élesíthető?",
      cell: ({ row }) => {
        const look = readyLook(row.original);
        return (
          <StatusPill tone={look.tone} title={look.title}>
            {look.label}
          </StatusPill>
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader
        back={{ to: "/flowk", label: "Flow-k" }}
        title="Összevetés a Klaviyóval"
        description={
          state.data
            ? `Az árnyék-flow-k döntései naponta összevetve azzal, amit a Klaviyo ténylegesen kiküldött. Élesíthető, ha az utolsó ${state.data.ready_days} végleges napon nincs nem magyarázott eltérés és hiányzó adat, és legalább ${state.data.ready_min_sends} küldést vetettünk össze.`
            : "Az árnyék-flow-k döntései naponta összevetve azzal, amit a Klaviyo ténylegesen kiküldött."
        }
      />
      <Toolbar className="mb-4">
        <Field label="Ország" htmlFor="compare-country">
          <NativeSelect id="compare-country" value={country} onChange={(event) => setCountry(event.target.value)}>
            <option value="">Mind</option>
            {COUNTRIES.map((code) => (
              <option key={code} value={code}>
                {code}
              </option>
            ))}
          </NativeSelect>
        </Field>
      </Toolbar>
      {state.error !== null && <ErrorState message={state.error} onRetry={reload} className="mb-4" />}
      <DataTable
        columns={columns}
        data={rows}
        getRowId={(row) => String(row.flow_id)}
        onRowClick={(row) => navigate(`/flowk/${row.flow_id}/osszevetes`)}
        loading={state.data === null && state.error === null}
        label="Összevetés"
        empty={
          <EmptyState
            bare
            title="Nincs mit összevetni"
            description="Egy flow sincs árnyékban, vagy a motor köre még nem vetett össze egy napot sem."
          />
        }
      />
    </div>
  );
}
